import Checkbox from "components/checkbox/Checkbox";
import Drawer from "components/drawer/Drawer";
import GearIcon from "icons/GearIcon";
import React from "react";
import { useDisplaySettings } from "./DisplaySettingsProvider";
import { useMeet } from "util/useMeet";
import { updateAttributesOnDocument } from "util/pouchActions";
import { useStatus } from "util/useStatus";
import Select from "components/select/Select";
import { toastr } from "react-redux-toastr";
import { useTheme } from "app/ThemeProvider";
import sortBy from "lodash/sortBy";
import { getDivisionDoc } from "util/lifterHelper";
import times from "lodash/times";
import UploadAttachment from "components/UploadAttachment";
import first from "lodash/first";

const components: Record<string, string> = {
  b: "Lifter Name",
  a: "Attempt Info",
  t: "Team Name",
  d: "Weight Class & Divisions",
  r: "Record Attempts",
  p: "Position Change",
  time: "Timer",
  l: "Ref Lights",
  c: "Loading Chart",
  o: "Lifting Order",
  n: "Next bar load",
  no: "Next Lifters",
  co: "Live Stream Bar",
  m: "Marquee",
  s: "Standings",
  i: "Image",
};

export const SettingsDrawer = () => {
  const { openThemeModal } = useTheme();
  const {
    editMode,
    setEditMode,
    setVisibility,
    visibility,
    maxRecordsToShow,
    setMaxRecordsToShow,
    maxDivisionsToShow,
    setMaxDivisionsToShow,
    marqueeSpeed,
    setMarqueeSpeed,
    standingsDivisionId,
    setStandingsDivisionId,
    standingsWeightClassId,
    setStandingsWeightClassId,
    standingsCount,
    setStandingsCount,
    imageSrc,
    setImageSrc,
    setIsTableSettingsModalOpen,
  } = useDisplaySettings();

  const meet = useMeet();

  const { isLocal } = useStatus();

  const setAsDefault = () => {
    updateAttributesOnDocument(meet._id, meet._id, {
      displayDefaultURL: window.location.search,
    });
    toastr.success("Display configuration set as meet default.", "");
  };

  const divisionOptions = React.useMemo(() => {
    const sortedDivisions = sortBy(meet.divisions, "name");

    return sortedDivisions.map((division) => {
      return { value: division._id, label: division.name };
    });
  }, [meet.divisions]);

  const weightClassOptions = React.useMemo(() => {
    const division = getDivisionDoc(standingsDivisionId, meet);
    if (division) {
      return Object.values(division.weightClasses).map((weightClass) => {
        return { value: weightClass._id, label: weightClass.name };
      });
    }

    return [];
  }, [meet, standingsDivisionId]);

  const imageOptions = React.useMemo(() => {
    const attachments = meet.attachments;

    if (!attachments) {
      return [];
    }

    return Object.values(attachments).map((a) => {
      const attachment = first(Object.keys(a?._attachments ?? {}));

      return { value: attachment, label: attachment };
    });
  }, [meet.attachments]);

  return (
    <Drawer
      contentLabel="Display Settings Drawer"
      trigger={
        <>
          <GearIcon />
        </>
      }
    >
      <div className="display-settings-modal-content">
        <Checkbox
          label="Edit Mode"
          value={editMode}
          onCheck={(v) => setEditMode(v)}
        />
        {isLocal && (
          <>
            <hr />
            <button onClick={setAsDefault}>Set as default</button>
          </>
        )}
        <hr />
        <button onClick={openThemeModal}>Customize Theme</button>
        <hr />

        <h2>Components</h2>
        {Object.keys(components).map((c) => {
          return (
            <div key={c}>
              <Checkbox
                key={c}
                label={components[c]}
                value={visibility[c]}
                onCheck={(v) =>
                  setVisibility((prev) => {
                    return { ...prev, [c]: v };
                  })
                }
              />
              {c === "r" && (
                <div style={{ marginLeft: 30, marginBottom: 12 }}>
                  <label>
                    Max Records To Show
                    <Select
                      clearable={false}
                      name="maxRecordsToShow"
                      value={maxRecordsToShow}
                      options={[
                        { label: "1", value: 1 },
                        { label: "2", value: 2 },
                        { label: "3", value: 3 },
                        { label: "4", value: 4 },
                        { label: "5", value: 5 },
                      ]}
                      onChange={(value) =>
                        typeof value === "number" && setMaxRecordsToShow(value)
                      }
                    />
                  </label>
                </div>
              )}
              {c === "d" && (
                <div style={{ marginLeft: 30, marginBottom: 12 }}>
                  <label>
                    Max Divisions To Show
                    <Select
                      clearable={false}
                      name="maxDivisionsToShow"
                      value={maxDivisionsToShow}
                      options={[
                        { label: "1", value: 1 },
                        { label: "2", value: 2 },
                        { label: "3", value: 3 },
                        { label: "4", value: 4 },
                        { label: "5", value: 5 },
                      ]}
                      onChange={(value) =>
                        typeof value === "number" &&
                        setMaxDivisionsToShow(value)
                      }
                    />
                  </label>
                </div>
              )}
              {c === "o" && (
                <div style={{ marginLeft: 30, marginBottom: 12 }}>
                  <button onClick={() => setIsTableSettingsModalOpen(true)}>
                    Open table settings
                  </button>
                </div>
              )}
              {c === "m" && (
                <div style={{ marginLeft: 30, marginBottom: 12 }}>
                  <label>
                    Speed
                    <Select
                      clearable={false}
                      name="marqueeSpeed"
                      value={marqueeSpeed}
                      options={[
                        { label: "Slow", value: 50 },
                        { label: "Medium", value: 100 },
                        { label: "Fast", value: 150 },
                        { label: "Really Fast", value: 250 },
                      ]}
                      onChange={(value) =>
                        typeof value === "number" && setMarqueeSpeed(value)
                      }
                    />
                  </label>
                </div>
              )}
              {c === "i" && (
                <div style={{ marginLeft: 30, marginBottom: 12 }}>
                  <label>
                    Image Source
                    <Select
                      clearable={true}
                      name="imageSrc"
                      value={imageSrc}
                      options={imageOptions}
                      onChange={(value) =>
                        typeof value === "string" && setImageSrc(value)
                      }
                    />
                  </label>
                  <div style={{ marginTop: 12 }}>
                    <UploadAttachment meet={meet} onComplete={setImageSrc} />
                  </div>
                </div>
              )}
              {c === "s" && (
                <div style={{ marginLeft: 30, marginBottom: 12 }}>
                  <label>
                    Show Top
                    <Select
                      clearable={false}
                      name="standingsCount"
                      value={standingsCount}
                      options={times(15, (i) => {
                        return { label: String(i + 1), value: i + 1 };
                      })}
                      onChange={(value) =>
                        typeof value === "number" && setStandingsCount(value)
                      }
                    />
                  </label>
                  <label>
                    Division
                    <Select
                      clearable={true}
                      name="standingsDivision"
                      value={standingsDivisionId}
                      options={divisionOptions}
                      onChange={(value) =>
                        typeof value === "string" &&
                        setStandingsDivisionId(value)
                      }
                    />
                  </label>
                  <label>
                    Weight Class
                    <Select
                      clearable={true}
                      name="standingsWeightClassId"
                      value={standingsWeightClassId}
                      options={weightClassOptions}
                      onChange={(value) =>
                        typeof value === "string" &&
                        setStandingsWeightClassId(value)
                      }
                    />
                  </label>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Drawer>
  );
};
